import React from 'react';
import SafeImage from "../../components/shared/SafeImage";
import DataTable from 'react-data-table-component';
import DummyIcon from '../../assets/images/user.jpg';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { supportList, removeSupportMessages, userEmailReply } from '../../api/admin';
import { Modal, ModalBody, ModalFooter, ModalHeader, Button } from 'reactstrap';
import Pagination from "react-js-pagination";
import { dateFormat, timeFormat } from '../../utils/method';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function createMarkup(html) {
  return { __html: html };
}

class SupportListing extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      activePage: 1,
      limit: 10,
      supportLists: [],
      sort: false,
      sortBy: null,
      // Reply lives here rather than on the separate /subscription-plan screen:
      // that screen was unreachable (nothing linked to it and its list action was
      // never dispatched) AND would have thrown, since it destructured a nested
      // `user` object the flat AdminSupportRow does not have.
      replyTo: null,
      replyMessage: '',
      replying: false
    };
  }

  getSupportList = async (sortDir = this.state.sort, sortBy = this.state.sortBy) => {
    const { activePage, limit } = this.state;
    const support = await this.props.supportList(activePage, limit, sortBy, sortDir);
    // supportList() resolves to the raw axios response; `.data` is
    // `{ data: AdminSupportRow[], meta: { total, ... } }`, not legacy's
    // `{ list, totalCounts }` — this was silently crashing before
    // setLoading(false) ran, leaving the loading mask stuck forever.
    if (support && support.data) {
      this.setState({
        supportLists: support.data.data || [],
        totalCounts: support.data.meta ? support.data.meta.total : 0,
      });
    }
    this.props.setLoading(false);
  }

  componentDidMount() {
    this.props.setLoading(true);
    this.props.setTitlePage('Support Management');
    this.getSupportList();
  }

  handlePageChange(pageNumber) {
    this.setState({ activePage: pageNumber }, () => {
      this.getSupportList();
    });
    this.props.setLoading(true);
  }

  onSort = (e) => {
    this.setState({
      sort: !this.state.sort,
      sortBy: e.selector
    }, () => {
      this.getSupportList(this.state.sort, e.selector === 'name' ? e.selector : null);
    });
    this.props.setLoading(true);
  }

  removeMessages = async (message) => {
    this.props.setLoading(true);
    // DELETE /admin/support/:id resolves with no body — show a fixed
    // confirmation instead of the (previously blank) response payload.
    await this.props.removeSupportMessages(message.id);
    toast('Support ticket removed');
    this.getSupportList();
    this.props.setLoading(false);
  }

  columns = [
    {
      name: 'Name & Email',
      selector: 'name',
      sortable: true,
      cell: row => <div className="user-wrapper">
        <span className="link-wrap">
          <SafeImage height="33" width="33" alt={row.authorName} src={row.authorAvatarUrl || DummyIcon} />
          <span className="user-detail">
            <span className="name">{row.authorName}</span>
            <span className="email-wrap">{row.authorEmail}</span>
          </span>
        </span>
      </div>
    },
    {
      name: 'Date & Time',
      selector: 'Datetime',
      sortable: true,
      cell: row => <span className="user-detail">
        <span className="name">{dateFormat(row.createdAt)}  |  {timeFormat(row.createdAt)}</span>
      </span>
    },
    {
      name: 'Subject',
      selector: 'subject'
    },
    {
      name: 'Message',
      selector: 'message',
      cell: (row) => <span dangerouslySetInnerHTML={createMarkup(row.message)}></span>
    },
    {
      name: 'Action',
      selector: 'action',
      center: true,
      cell: (row) => (
        <>
          <span className="remove" style={{ marginRight: 12 }} onClick={() => this.openReply(row)}>Reply</span>
          <span className="remove" onClick={() => this.removeMessages(row)}>Remove</span>
        </>
      )
    }
  ];

  openReply = (row) => this.setState({ replyTo: row, replyMessage: '' });

  closeReply = () => this.setState({ replyTo: null, replyMessage: '' });

  sendReply = async () => {
    const { replyTo, replyMessage } = this.state;
    if (!replyTo || !replyMessage.trim()) {
      toast('Write a reply first', { type: 'error' });
      return;
    }
    this.setState({ replying: true });
    const sent = await this.props.userEmailReply(replyTo.id, replyMessage.trim());
    this.setState({ replying: false });
    // Check the STATUS, not `sent.data`. `userEmailReply` resolves with
    // `error.response` on failure, and an error response still carries a `data`
    // body — so `sent.data` is truthy for every 4xx/5xx and this screen toasted
    // "Reply sent" while the email had not been sent. The API's own
    // "email not configured" BadRequest is exactly the case staff must see.
    const ok = sent && sent.status >= 200 && sent.status < 300;
    if (ok) {
      toast('Reply sent', { type: 'success' });
      this.closeReply();
    } else {
      const reason = sent?.data?.message;
      toast(reason ? `Failed to send the reply: ${reason}` : 'Failed to send the reply', {
        type: 'error',
      });
    }
  };

  render() {
    const { totalCounts, activePage, limit, supportLists } = this.state;
    return (
      <>
        <DataTable
          title="Support"
          columns={this.columns}
          className="custom-table with-5-col"
          data={supportLists}
          onSort={this.onSort}
        />
        <Modal isOpen={!!this.state.replyTo} toggle={this.closeReply}>
          <ModalHeader toggle={this.closeReply}>
            Reply to {this.state.replyTo ? this.state.replyTo.authorName : ''}
          </ModalHeader>
          <ModalBody>
            {/* The admin's global CSS makes `.modal-body` a ROW flex container, so
                its children lay out side by side — "Subject:", "To:" and the
                textarea all ended up on one line. One wrapper that is itself a
                COLUMN restores normal stacking without touching global styles. */}
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <div style={{ marginBottom: 8 }}>
                <strong>Subject:</strong>{' '}
                {this.state.replyTo ? this.state.replyTo.subject : ''}
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>To:</strong>{' '}
                {this.state.replyTo ? this.state.replyTo.authorEmail : ''}
              </div>
              <textarea
                className="form-control"
                style={{ width: '100%' }}
                rows={5}
                value={this.state.replyMessage}
                placeholder="Write your reply…"
                onChange={(e) => this.setState({ replyMessage: e.target.value })}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={this.closeReply}>Cancel</Button>
            <Button color="primary" disabled={this.state.replying} onClick={this.sendReply}>
              {this.state.replying ? 'Sending…' : 'Send reply'}
            </Button>
          </ModalFooter>
        </Modal>
        {totalCounts > this.state.limit && <Pagination
          activePage={activePage}
          itemsCountPerPage={limit}
          totalItemsCount={totalCounts}
          pageRangeDisplayed={5}
          onChange={this.handlePageChange.bind(this)}
        />}
      </>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    admin: state.user,
    userSupportData: state.supports
  }
}

export default connect(mapStateToProps, {
  supportList,
  removeSupportMessages,
  userEmailReply
})(withRouter(SupportListing));

